// Lightweight client-side error reporter.
//
// Installs global handlers that POST uncaught errors and unhandled promise
// rejections to /api/errors/js, where the Go server appends them to
// monitoring/logs/frontend_error_reports.jsonl. The frontend error monitor then
// enriches each minified stack frame with source maps and dispatches a codex
// agent to auto-fix. Reporting is strictly best-effort: it must never throw and
// must never become its own error source.

export type ErrorKind = 'error' | 'unhandledrejection' | 'react' | 'manual';

export interface ErrorReport {
  message: string;
  kind: ErrorKind;
  stack?: string;
  source?: string;
  line?: number;
  column?: number;
  componentStack?: string;
  url: string;
  userAgent: string;
  release?: string;
  ts: string;
}

const ENDPOINT = '/api/errors/js';
const MAX_REPORTS_PER_SESSION = 25;
const seen = new Set<string>();
let sent = 0;

function fingerprint(r: Pick<ErrorReport, 'kind' | 'message' | 'source' | 'line'>): string {
  return `${r.kind}|${r.message}|${r.source ?? ''}:${r.line ?? ''}`;
}

function send(report: ErrorReport): void {
  const fp = fingerprint(report);
  if (seen.has(fp) || sent >= MAX_REPORTS_PER_SESSION) return;
  seen.add(fp);
  sent += 1;
  try {
    const body = JSON.stringify(report);
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'application/json' }));
    } else {
      void fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
      }).catch(() => {});
    }
  } catch {
    /* swallow — reporting must never throw */
  }
}

/** Report an error explicitly (used by the React ErrorBoundary, and available for manual use). */
export function reportError(
  partial: Partial<ErrorReport> & { message: string; kind: ErrorKind },
): void {
  if (typeof window === 'undefined') return;
  send({
    url: window.location.href,
    userAgent: navigator.userAgent,
    release: (import.meta as unknown as { env?: Record<string, string> }).env?.VITE_RELEASE,
    ts: new Date().toISOString(),
    ...partial,
  });
}

let installed = false;

/** Install global window error / unhandledrejection handlers. Idempotent; no-op during SSR. */
export function installErrorReporter(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  window.addEventListener('error', (e: ErrorEvent) => {
    // Resource load errors (e.g. <img>) fire ErrorEvent with no `error` and an
    // empty message — skip those to avoid noise.
    if (!e.message && !e.error) return;
    reportError({
      kind: 'error',
      message: e.message || (e.error && e.error.message) || 'Unknown error',
      source: e.filename,
      line: e.lineno,
      column: e.colno,
      stack: e.error?.stack,
    });
  });

  window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
    const reason = e.reason as { message?: string; stack?: string } | undefined;
    reportError({
      kind: 'unhandledrejection',
      message: (reason && (reason.message || String(reason))) || 'Unhandled promise rejection',
      stack: reason?.stack,
    });
  });
}
